import React, { useState, useEffect } from "react";
import { supabase } from "../../supabase";
import Navbar from "../Navbar/Navbar";
import "./NewBookings.css";

const NewBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("sent");

  const defaultAvatar =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 24 24' fill='%23999'%3E%3Cpath d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z'/%3E%3C/svg%3E";

  useEffect(() => {
    initializeUser();
  }, []);

  useEffect(() => {
    if (userId) {
      fetchBookings();
    }
  }, [userId, filter, activeTab]);

  const initializeUser = async () => {
    try {
      const { data, error } = await supabase.auth.getUser();
      if (error) throw error;
      if (data?.user) setUserId(data.user.id);
    } catch (error) {
      console.error("Error getting user:", error);
    }
    setLoading(false);
  };

  const fetchBookings = async () => {
    try {
      let query = supabase.from("booking_requests").select("*");

      if (activeTab === "received") {
        query = query.eq("receiver_id", userId);
      } else {
        query = query.eq("sender_id", userId);
      }

      if (filter === "in_progress") {
        query = query.eq("status", "pending");
      } else if (filter === "upcoming") {
        const now = new Date().toISOString();
        query = query
          .eq("status", "accepted")
          .not("start_datetime", "is", null)
          .gte("end_datetime", now);
      } else if (filter === "past") {
        const now = new Date().toISOString();
        query = query.or(
          `status.eq.rejected,and(status.eq.accepted,end_datetime.not.is.null,end_datetime.lt.${now})`
        );
      }

      const { data: bookingData, error } = await query.order("created_at", {
        ascending: false,
      });

      if (error) throw error;

      const bookingsWithProfiles = await Promise.all(
        (bookingData || []).map(async (booking) => {
          try {
            const { data: senderProfile } = await supabase
              .from("user_profiles")
              .select("full_name, avatar_url")
              .eq("user_id", booking.sender_id)
              .single();

            const { data: receiverProfile } = await supabase
              .from("user_profiles")
              .select("full_name, avatar_url")
              .eq("user_id", booking.receiver_id)
              .single();

            return {
              ...booking,
              sender: {
                user_id: booking.sender_id,
                full_name: senderProfile?.full_name || "Unknown User",
                avatar_url: senderProfile?.avatar_url || null,
              },
              receiver: {
                user_id: booking.receiver_id,
                full_name: receiverProfile?.full_name || "Unknown User",
                avatar_url: receiverProfile?.avatar_url || null,
              },
            };
          } catch {
            return {
              ...booking,
              sender: { user_id: booking.sender_id, full_name: "Unknown User", avatar_url: null },
              receiver: { user_id: booking.receiver_id, full_name: "Unknown User", avatar_url: null },
            };
          }
        })
      );

      setBookings(bookingsWithProfiles);

      if (activeTab === "received" && filter === "in_progress") {
        await supabase
          .from("booking_requests")
          .update({ provider_viewed: true })
          .eq("receiver_id", userId)
          .eq("status", "pending")
          .eq("provider_viewed", false);
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
      setBookings([]);
    }
  };

  const handleBookingAction = async (bookingId, action) => {
    try {
      const { error } = await supabase
        .from("booking_requests")
        .update({ status: action, updated_at: new Date().toISOString() })
        .eq("id", bookingId);

      if (error) throw error;

      setBookings((prev) =>
        prev.map((b) => (b.id === bookingId ? { ...b, status: action } : b))
      );
    } catch (error) {
      console.error(`Error ${action}ing booking:`, error);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    const diffInDays = diffInHours / 24;
    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } else if (diffInDays < 7) {
      return date.toLocaleDateString([], { weekday: "short" });
    } else {
      return date.toLocaleDateString([], { month: "short", day: "numeric" });
    }
  };

  const truncateMessage = (message, maxLength = 80) => {
    if (!message) return "";
    return message.length > maxLength
      ? message.substring(0, maxLength) + "..."
      : message;
  };

  const getOtherUser = (booking) =>
    activeTab === "received" ? booking.sender : booking.receiver;

  const getStatusClass = (status) => {
    if (status === "accepted") return "accepted";
    if (status === "rejected") return "rejected";
    return "pending";
  };

  const handleCardClick = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const tabs = [
    { key: "sent", label: "Made" },
    { key: "received", label: "Received" },
  ];

  const filters = [
    { key: "all", label: "All" },
    { key: "in_progress", label: "In Progress" },
    { key: "upcoming", label: "Upcoming" },
    { key: "past", label: "Past" },
  ];

  const filterLabel =
    filter === "in_progress" ? "in progress" : filter !== "all" ? filter : "";

  if (loading) {
    return <div className="invites-loading">Loading invites...</div>;
  }

  return (
    <>
      <Navbar />
      <div className="invites-root">
        {/* Hero */}
        <div className="invites-hero">
          <div className="invites-hero-inner">
            <h1 className="invites-heading">Invites</h1>

            {/* Tab pills */}
            <div className="invites-tabs">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  className={`invites-pill${activeTab === t.key ? " active" : ""}`}
                  onClick={() => setActiveTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Filter pills */}
            <div className="invites-filters">
              {filters.map((f) => (
                <button
                  key={f.key}
                  className={`invites-pill${filter === f.key ? " active" : ""}`}
                  onClick={() => setFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="invites-results">
          <div className="invites-col">
            <p className="invites-count">
              {bookings.length} {filterLabel} {bookings.length === 1 ? "invite" : "invites"}
            </p>

            {bookings.length === 0 ? (
              <div className="invites-empty">
                <div className="invites-empty-icon">
                  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </div>
                <h3 className="invites-empty-title">No invites yet</h3>
                <p className="invites-empty-msg">
                  {activeTab === "received"
                    ? "Invites you receive will appear here."
                    : "Invites you send will appear here."}
                </p>
              </div>
            ) : (
              <div className="invites-card-list">
                {bookings.map((booking) => {
                  const otherUser = getOtherUser(booking);
                  const isExpanded = expandedId === booking.id;
                  return (
                    <div
                      key={booking.id}
                      className={`invites-card${isExpanded ? " expanded" : ""}`}
                      onClick={() => handleCardClick(booking.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === "Enter" && handleCardClick(booking.id)}
                    >
                      {/* Card header */}
                      <div className="invites-card-header">
                        <img
                          src={otherUser?.avatar_url || defaultAvatar}
                          alt={otherUser?.full_name}
                          className="invites-avatar"
                          onError={(e) => { e.target.src = defaultAvatar; }}
                        />
                        <div className="invites-card-body">
                          <div className="invites-card-top">
                            <span className="invites-card-name">
                              {otherUser?.full_name || "Unknown User"}
                            </span>
                            <span className="invites-card-date">
                              {formatTime(booking.created_at)}
                            </span>
                          </div>
                          <div className={`invites-status-badge ${getStatusClass(booking.status)}`}>
                            <span className="invites-status-dot" />
                            {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                          </div>
                          <p className="invites-card-preview">
                            {truncateMessage(booking.message)}
                          </p>
                        </div>
                        <svg
                          className={`invites-chevron${isExpanded ? " open" : ""}`}
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="6,9 12,15 18,9" />
                        </svg>
                      </div>

                      {/* Expanded details */}
                      {isExpanded && (
                        <div className="invites-details" onClick={(e) => e.stopPropagation()}>
                          {/* Message */}
                          <div className="invites-details-section">
                            <h4 className="invites-details-label">Message</h4>
                            <p className="invites-details-value">{booking.message}</p>
                          </div>

                          {/* Event details */}
                          {booking.start_datetime && booking.end_datetime && (
                            <div className="invites-details-section">
                              <h4 className="invites-details-label">Event Details</h4>
                              <p className="invites-event-date">
                                <strong>From:</strong>{" "}
                                {new Date(booking.start_datetime).toLocaleDateString("en-GB", {
                                  weekday: "long", year: "numeric", month: "long", day: "numeric",
                                })}{" "}
                                at{" "}
                                {new Date(booking.start_datetime).toLocaleTimeString("en-GB", {
                                  hour: "2-digit", minute: "2-digit",
                                })}
                              </p>
                              <p className="invites-event-date">
                                <strong>Until:</strong>{" "}
                                {new Date(booking.end_datetime).toLocaleDateString("en-GB", {
                                  weekday: "long", year: "numeric", month: "long", day: "numeric",
                                })}{" "}
                                at{" "}
                                {new Date(booking.end_datetime).toLocaleTimeString("en-GB", {
                                  hour: "2-digit", minute: "2-digit",
                                })}
                              </p>
                              {booking.location && (
                                <p className="invites-event-date">
                                  <strong>Location:</strong>{" "}
                                  {booking.location.toLowerCase().includes("online") ||
                                  booking.location.toLowerCase().includes("virtual") ||
                                  booking.location.toLowerCase().includes("zoom") ||
                                  booking.location.toLowerCase().includes("teams") ||
                                  booking.location.toLowerCase().includes("meet") ? (
                                    <span className="location-virtual">{booking.location}</span>
                                  ) : (
                                    <a
                                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(booking.location)}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="location-link"
                                    >
                                      {booking.location} 📍
                                    </a>
                                  )}
                                </p>
                              )}
                              {booking.timezone && (
                                <p className="invites-timezone">Timezone: {booking.timezone}</p>
                              )}
                            </div>
                          )}

                          {/* Participants */}
                          <div className="invites-details-section">
                            <h4 className="invites-details-label">Participants</h4>
                            <p className="invites-details-value">
                              <strong>Requester:</strong> {booking.sender?.full_name || "Unknown"}
                            </p>
                            <p className="invites-details-value">
                              <strong>Speaker:</strong> {booking.receiver?.full_name || "Unknown"}
                            </p>
                          </div>

                          {/* Dates */}
                          <div className="invites-details-section">
                            <p className="invites-details-value" style={{ fontSize: "0.8rem", color: "#9ca3af" }}>
                              Requested: {new Date(booking.created_at).toLocaleDateString()}
                              {booking.updated_at !== booking.created_at && (
                                <> · Updated: {new Date(booking.updated_at).toLocaleDateString()}</>
                              )}
                            </p>
                          </div>

                          {/* Accept / Decline */}
                          {activeTab === "received" && booking.status === "pending" && (
                            <div className="invites-actions">
                              <button
                                className="invites-btn invites-btn-accept"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleBookingAction(booking.id, "accepted");
                                }}
                              >
                                Accept
                              </button>
                              <button
                                className="invites-btn invites-btn-decline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleBookingAction(booking.id, "rejected");
                                }}
                              >
                                Decline
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default NewBookings;
